/** The words of a self-registration link (plan B3), one module for the tour-wide list, the
 *  date-specific grid, the menu, the revoke confirm and the settings dialog, so a link
 *  never reads two ways.
 *
 *  Instants (`created_ts`, `revoked_ts`) show in the browser's zone with the UTC instant in
 *  the tooltip (the grids' rule); a link's `date` is a travel date (a wall date, never
 *  converted). */
import type { TourSelfregLink } from '@/api/tours';
import { formatWallDate } from '@/features/records/editor/format';
import { formatRelativeTime } from '@/utils/format';

/** Past this many days a created time shows as a date (the relative time in its tooltip). */
export const ABSOLUTE_AFTER_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

const fullDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const shortDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const dateTime = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function valid(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `…9d2aed6a1b6c`: the last group of the link's UUID — what tells links apart. */
export function addressTail(link: Pick<TourSelfregLink, 'id'>): string {
  const last = link.id.split('-').pop() ?? link.id;
  return `…${last}`;
}

/** "5 Oct 2026". */
export function formatFullDate(iso: string | null | undefined): string {
  const d = valid(iso);
  return d ? fullDate.format(d) : '—';
}

/** "5 Oct" this year, "5 Oct 2025" otherwise. */
export function formatShortDate(iso: string | null | undefined, now: Date = new Date()): string {
  const d = valid(iso);
  if (!d) return '—';
  return d.getFullYear() === now.getFullYear() ? shortDate.format(d) : fullDate.format(d);
}

/** The Created cell: a relative time for about a month, then the date; the other form
 *  (and the UTC instant) in the tooltip. */
export function createdWords(iso: string | null | undefined, now: Date = new Date()): { text: string; tooltip: string } {
  const d = valid(iso);
  if (!d) return { text: '—', tooltip: 'Creation time not recorded' };
  const utc = `${d.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
  const relative = formatRelativeTime(iso);
  if (now.getTime() - d.getTime() > ABSOLUTE_AFTER_DAYS * DAY_MS) {
    return { text: fullDate.format(d), tooltip: `Created ${relative} · ${utc}` };
  }
  return { text: relative, tooltip: `Created ${dateTime.format(d)} · ${utc}` };
}

/** "Email · Phone" / "Email only" / "Phone only" / "No verification" — what the guest
 *  page applies — and whether the link follows the tour or sets its own. */
export function verificationWords(
  link: Pick<TourSelfregLink, 'effective_verify_email' | 'effective_verify_phone' | 'verify_email' | 'verify_phone'>,
): { text: string; source: 'follows tour' | 'set on link' } {
  const e = link.effective_verify_email;
  const p = link.effective_verify_phone;
  const text = e && p ? 'Email · Phone' : e ? 'Email only' : p ? 'Phone only' : 'No verification';
  const source = link.verify_email === null && link.verify_phone === null ? 'follows tour' : 'set on link';
  return { text, source };
}

/** The verification as a sentence (the tooltip over the short words). */
export function verificationSentence(
  link: Pick<TourSelfregLink, 'effective_verify_email' | 'effective_verify_phone' | 'verify_email' | 'verify_phone'>,
): string {
  const v = verificationWords(link);
  const what =
    v.text === 'Email · Phone'
      ? 'Guests verify their email and phone'
      : v.text === 'Email only'
        ? 'Guests verify their email'
        : v.text === 'Phone only'
          ? 'Guests verify their phone'
          : 'Guests verify nothing';
  return `${what} — ${v.source === 'follows tour' ? 'this link follows the tour’s setting' : 'set on this link'}.`;
}

/** "12 registered" / "Unused". */
export function registeredWords(count: number): string {
  return count > 0 ? `${count} registered` : 'Unused';
}

/** Under a "Registered" column header: "12" / "Unused". */
export function registeredShort(count: number): string {
  return count > 0 ? String(count) : 'Unused';
}

/** Why a revoked link offers no Copy / QR. */
export const REVOKED_NO_COPY = 'Revoked — restore it first';

/** The link's scope: "any date" or its travel date. */
export function scopeWords(link: Pick<TourSelfregLink, 'date'>): string {
  return link.date ? `for ${formatWallDate(link.date)}` : 'any date';
}

export const KIND_LABEL: Record<TourSelfregLink['kind'], string> = {
  master: 'Master',
  custom: 'Custom',
  guide: 'Guide',
  observer: 'Observer',
};

/** A row's accessible name: "Link created 5 Oct 2026, any date, 12 registered". */
export function linkRowName(link: TourSelfregLink, now: Date = new Date()): string {
  const parts = [
    `${link.is_master ? 'Master link' : 'Link'} created ${formatFullDate(link.created_ts)}`,
    scopeWords(link),
    link.registered_count > 0 ? `${link.registered_count} registered` : 'unused',
  ];
  if (link.revoked_ts) parts.push(`revoked ${formatShortDate(link.revoked_ts, now)}`);
  return parts.join(', ');
}

/** Only an active custom link without a date can become the master (the API's rule). */
export function canBecomeMaster(link: TourSelfregLink): boolean {
  return link.kind === 'custom' && !link.revoked_ts && link.date === null && !link.is_master;
}

/** The successors a master revoke may name: the active custom undated links. */
export function successorsOf(master: TourSelfregLink, links: readonly TourSelfregLink[]): TourSelfregLink[] {
  return links.filter((l) => l.id !== master.id && canBecomeMaster(l));
}

/** The words of a verification choice: null = follow the tour. */
export function verifyChoiceWords(value: boolean | null): string {
  return value === null ? "Tour's setting" : value ? 'Verify' : "Don't verify";
}

/** What "Tour's setting" resolves to for one verification: the link's effective value
 *  while it follows the tour, else the tour's stored setting; null when the tour itself
 *  follows the operator (whose setting the console does not read). */
export function tourVerifyResolves(
  linkValue: boolean | null,
  effective: boolean,
  tourValue: boolean | null,
): boolean | null {
  if (linkValue === null) return effective;
  return tourValue;
}
